import React, { useState, useEffect, useRef, useCallback } from 'react';
import axios from 'axios';
import { Input } from './ui/input';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * RecipientPicker — a To-field that supports typing emails directly OR
 * starting to type a personnel name to autocomplete + insert their email.
 *
 * The component is otherwise a drop-in replacement for a plain <Input value
 * onChange />: parent owns the raw string, child shows the dropdown only when
 * the rightmost token (after the last comma/semicolon) looks like a name
 * rather than an email.
 */
function lastTokenOf(s) {
  const parts = String(s || '').split(/[,;]/);
  return parts[parts.length - 1].trim();
}

function replaceLastToken(s, replacement) {
  const idx = Math.max(s.lastIndexOf(','), s.lastIndexOf(';'));
  if (idx < 0) return replacement;
  return s.slice(0, idx + 1) + ' ' + replacement;
}

export default function RecipientPicker({ value, onChange, placeholder, disabled, ...rest }) {
  const [matches, setMatches] = useState([]);
  const [showMenu, setShowMenu] = useState(false);
  const [activeIdx, setActiveIdx] = useState(0);
  const inputRef = useRef(null);
  const searchTimerRef = useRef(null);

  const search = useCallback(async (q) => {
    if (!q || q.length < 2) {
      setMatches([]);
      return;
    }
    // If the current token already includes an "@" assume the user is typing
    // a raw email — no need to fetch.
    if (q.includes('@')) {
      setMatches([]);
      return;
    }
    try {
      const res = await axios.get(`${API}/personnel/search`, {
        params: { q, limit: 8 },
      });
      const hits = (res.data || []).filter(p => p.email || p.work_email);
      setMatches(hits);
      setActiveIdx(0);
    } catch {
      setMatches([]);
    }
  }, []);

  // Debounce 200ms — feels responsive without spamming the server
  useEffect(() => {
    if (searchTimerRef.current) clearTimeout(searchTimerRef.current);
    const tok = lastTokenOf(value);
    searchTimerRef.current = setTimeout(() => search(tok), 200);
    return () => clearTimeout(searchTimerRef.current);
  }, [value, search]);

  const choose = (person) => {
    const email = person.email || person.work_email;
    if (!email) return;
    const fullName = `${person.first_name || ''} ${person.last_name || ''}`.trim();
    const formatted = fullName ? `"${fullName}" <${email}>` : email;
    const next = replaceLastToken(value, formatted) + ', ';
    onChange(next);
    setMatches([]);
    setShowMenu(false);
    setTimeout(() => inputRef.current?.focus(), 0);
  };

  const handleKeyDown = (e) => {
    if (!matches.length || !showMenu) return;
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setActiveIdx((i) => (i + 1) % matches.length);
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setActiveIdx((i) => (i - 1 + matches.length) % matches.length);
    } else if (e.key === 'Enter' || e.key === 'Tab') {
      e.preventDefault();
      choose(matches[activeIdx]);
    } else if (e.key === 'Escape') {
      setShowMenu(false);
    }
  };

  return (
    <div className="relative flex-1">
      <Input
        ref={inputRef}
        value={value}
        onChange={e => { onChange(e.target.value); setShowMenu(true); }}
        onFocus={() => setShowMenu(true)}
        onBlur={() => setTimeout(() => setShowMenu(false), 150)}
        onKeyDown={handleKeyDown}
        placeholder={placeholder}
        disabled={disabled}
        {...rest}
      />
      {showMenu && matches.length > 0 && (
        <div
          className="absolute left-0 right-0 top-full mt-1 z-50 bg-white border rounded-md shadow-lg max-h-64 overflow-y-auto"
          data-testid="recipient-picker-menu"
        >
          {matches.map((p, i) => {
            const email = p.email || p.work_email;
            const name = `${p.first_name || ''} ${p.last_name || ''}`.trim();
            return (
              <button
                key={p.id || email}
                type="button"
                onMouseDown={(e) => { e.preventDefault(); choose(p); }}
                onMouseEnter={() => setActiveIdx(i)}
                className={`w-full text-left px-3 py-2 text-sm border-b last:border-b-0 ${i === activeIdx ? 'bg-muted' : 'hover:bg-muted/50'}`}
                data-testid={`recipient-picker-option-${i}`}
              >
                <div className="font-medium">{name || email}</div>
                <div className="text-xs text-muted-foreground">
                  {email}
                  {p.discipline ? ` · ${p.discipline}` : ''}
                </div>
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}
